import React, { useState, useRef, useEffect } from 'react';
import { ShoppingCart, User, Heart, Search, Package, Truck, Plus, Menu, X, Star, Shield, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Template 13: CRAFT - Artisan/Handmade, Warm & Natural (Inspired by Shopify's Craft theme)
const Template13 = () => {
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    const toggleWishlist = (id) => setWishlist(prev => ({ ...prev, [id]: !prev[id] }));

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial(prev => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial(prev =>
                diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length
            );
        }
    };

    const categories = [
        { title: 'Minimal Knits', image: 'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=600&h=800&fit=crop', count: '24 pieces' },
        { title: 'Clean Lines', image: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=600&h=800&fit=crop', count: '18 pieces' },
        { title: 'Nordic Layers', image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=600&h=800&fit=crop', count: '32 pieces' },
        { title: 'Essentials', image: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&h=800&fit=crop', count: '45 pieces' },
    ];

    const products = [
        { id: 1, name: 'Merino Wool Sweater', price: 189, oldPrice: 229, image: 'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=500&h=650&fit=crop', badge: 'Sale' },
        { id: 2, name: 'Organic Cotton Tee', price: 59, oldPrice: null, image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500&h=650&fit=crop', badge: 'New' },
        { id: 3, name: 'Linen Blend Trousers', price: 139, oldPrice: null, image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=500&h=650&fit=crop', badge: null },
        { id: 4, name: 'Cashmere Scarf', price: 99, oldPrice: 129, image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=650&fit=crop', badge: 'Sale' },
        { id: 5, name: 'Structured Blazer', price: 249, oldPrice: null, image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=500&h=650&fit=crop', badge: 'New' },
        { id: 6, name: 'Minimalist Watch', price: 179, oldPrice: null, image: 'https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=500&h=650&fit=crop', badge: null },
    ];

    const testimonials = [
        { text: 'The quality is unmatched. Every piece feels intentional and beautifully made.', name: 'Emma Lindqvist', location: 'Stockholm' },
        { text: 'Finally, a brand that understands less is more. My entire wardrobe is Zenith now.', name: 'Jonas Berg', location: 'Copenhagen' },
        { text: 'Timeless pieces that transcend seasons. This is what sustainable fashion looks like.', name: 'Astrid Noren', location: 'Oslo' },
    ];

    const features = [
        { icon: Package, title: 'Sustainable', desc: 'Eco-friendly materials' },
        { icon: Truck, title: 'Free Shipping', desc: 'Orders over $100' },
        { icon: Shield, title: 'Quality Promise', desc: '2-year guarantee' },
        { icon: Heart, title: 'Handcrafted', desc: 'Artisan made' },
    ];

    const instagramImages = [
        'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=300&h=300&fit=crop',
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-serif">
            {/* === HEADER === */}
            {/* Mobile Header */}
            <header className="lg:hidden bg-white border-b border-gray-100 sticky top-0 z-50">
                <div className="px-4 sm:px-6">
                    <div className="flex justify-between items-center py-3 sm:py-4">
                        <button className="min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                            {mobileMenuOpen ? <X className="w-5 h-5 text-gray-900" /> : <Menu className="w-5 h-5 text-gray-900" />}
                        </button>
                        <h1 onClick={() => navigate('/template13')} className="text-xl sm:text-2xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            ESSENCE
                        </h1>
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="relative cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => navigate('/template13/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute top-1 right-0 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                        </div>
                    </div>
                </div>
                {mobileMenuOpen && (
                    <div className="bg-white border-t border-gray-200">
                        <nav className="px-4 py-6 space-y-1">
                            {['Home', 'Shop', 'Contact', 'Orders'].map(item => (
                                <button key={item} onClick={() => { navigate(`/template13${item === 'Home' ? '' : '/' + item.toLowerCase()}`); setMobileMenuOpen(false); }} className="block w-full text-left text-base text-gray-800 py-3 min-h-[44px]">{item}</button>
                            ))}
                            <div className="flex items-center gap-6 pt-4 border-t border-gray-200">
                                <Search className="w-5 h-5 text-gray-900 cursor-pointer" />
                                <Heart className="w-5 h-5 text-gray-900 cursor-pointer" />
                                <User className="w-5 h-5 text-gray-900 cursor-pointer" onClick={() => navigate('/template13/profile')} />
                            </div>
                        </nav>
                    </div>
                )}
            </header>

            {/* Desktop Header - EXACT original */}
            <header className="hidden lg:block bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template13')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            ESSENCE
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template13/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template13/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template13')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template13/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template13/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template13/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* === HERO === */}
            {/* Mobile Hero */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/5]">
                    <img src="https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=1920&q=80" alt="Collection" className="w-full h-full object-cover" />
                </div>
                <div className="bg-gray-50 px-5 py-8">
                    <p className="text-xs tracking-[0.3em] text-gray-400 mb-3 uppercase">Handcrafted with Love</p>
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight mb-4">Made by Hand,<br/>Made with Heart</h2>
                    <p className="text-[14px] text-gray-600 leading-relaxed mb-6">Each piece is carefully crafted by skilled artisans using traditional techniques passed down through generations.</p>
                    <button onClick={() => navigate('/template13/shop')} className="w-full py-4 bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors min-h-[48px] rounded-sm">Shop Collection</button>
                </div>
            </section>

            {/* Desktop Hero - EXACT original */}
            <section className="hidden lg:block relative bg-white">
                <div className="max-w-6xl mx-auto px-6 py-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        <div>
                            <p className="text-sm text-gray-600 mb-4 tracking-wide uppercase">Handcrafted with Love</p>
                            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                                Made by Hand,<br/>Made with Heart
                            </h2>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                Each piece is carefully crafted by skilled artisans using traditional techniques
                                passed down through generations. Discover unique creations that tell a story.
                            </p>
                            <button className="px-8 py-4 bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors rounded-sm">
                                Shop Collection
                            </button>
                        </div>
                        <div className="relative">
                            <img
                                src="https://placehold.co/800x600/E0E0E0/333333?text=Artisan+Craft"
                                alt="Artisan Craft"
                                className="w-full h-[500px] object-cover rounded-sm shadow-xl"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-white p-6 shadow-lg rounded-sm max-w-xs">
                                <p className="text-sm text-gray-900 font-medium">
                                    "Every stitch, every detail is a labor of love." - Master Artisan
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* === CATEGORIES === */}
            {/* Mobile Categories - horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16">
                <div className="px-5 mb-8">
                    <div className="text-center">
                        <h2 className="text-xl sm:text-2xl font-bold text-amber-950 mb-2">Our Collections</h2>
                        <p className="text-sm text-amber-700">Handpicked selections of artisan goods</p>
                    </div>
                </div>
                <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                    {categories.map((cat, i) => (
                        <div key={i} className="w-[68vw] flex-shrink-0 snap-start cursor-pointer group">
                            <div className="relative rounded-2xl overflow-hidden aspect-[3/4]">
                                <img src={cat.image} alt={cat.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent" />
                                <div className="absolute bottom-4 left-4 right-4">
                                    <h3 className="text-white font-medium text-base mb-1">{cat.title}</h3>
                                    <p className="text-gray-300 text-xs">{cat.count}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="w-4 flex-shrink-0" />
                </div>
            </section>

            {/* Desktop Categories - EXACT original */}
            <section className="hidden lg:block max-w-6xl mx-auto px-6 py-20">
                <div className="text-center mb-12">
                    <h2 className="text-4xl font-bold text-amber-950 mb-4">Our Collections</h2>
                    <p className="text-lg text-amber-700">Handpicked selections of artisan goods</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { title: 'Pottery', image: 'https://images.unsplash.com/photo-1493106641515-6b5631de4bb9?w=600&h=800&fit=crop', desc: 'Hand-thrown ceramics' },
                        { title: 'Textiles', image: 'https://images.unsplash.com/photo-1558769132-cb1aea6c43e4?w=600&h=800&fit=crop', desc: 'Woven with tradition' },
                        { title: 'Woodwork', image: 'https://images.unsplash.com/photo-1533090161767-e6ffed986c88?w=600&h=800&fit=crop', desc: 'Carved to perfection' }
                    ].map((cat, index) => (
                        <div key={index} className="group cursor-pointer bg-white rounded-sm overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                            <div className="relative overflow-hidden aspect-[3/4]">
                                <img
                                    src={cat.image}
                                    alt={cat.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                            <div className="p-6 text-center">
                                <h3 className="text-2xl font-bold text-amber-950 mb-2">{cat.title}</h3>
                                <p className="text-amber-700 mb-4">{cat.desc}</p>
                                <button className="text-amber-900 font-medium border-b-2 border-amber-900 hover:border-amber-700 transition-colors">
                                    Browse Collection
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* === PRODUCTS === */}
            {/* Mobile Products - horizontal scroll with wishlist/quick-add */}
            <section className="lg:hidden py-10 sm:py-16 bg-white">
                <div className="px-5 mb-8">
                    <div className="flex items-end justify-between">
                        <div>
                            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">All Products</h2>
                            <p className="text-sm text-gray-600 mt-1">Discover our latest collection</p>
                        </div>
                    </div>
                </div>
                <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                    {products.map((p) => (
                        <div key={p.id} className="w-[44vw] flex-shrink-0 snap-start">
                            <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-gray-100 mb-3">
                                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                                {p.badge && <span className="absolute top-2 left-2 bg-gray-900 text-white text-[10px] px-2 py-0.5 rounded-full">{p.badge}</span>}
                                <button onClick={() => toggleWishlist(p.id)} className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                                    <Heart className={`w-3.5 h-3.5 ${wishlist[p.id] ? 'fill-gray-900 text-gray-900' : 'text-gray-600'}`} />
                                </button>
                                <button className="absolute bottom-2 right-2 w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center">
                                    <Plus className="w-3.5 h-3.5 text-white" />
                                </button>
                            </div>
                            <h3 className="text-xs font-medium text-gray-900 line-clamp-1 mb-1">{p.name}</h3>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-gray-900">${p.price}</span>
                                {p.oldPrice && <span className="text-[10px] text-gray-400 line-through">${p.oldPrice}</span>}
                            </div>
                        </div>
                    ))}
                    <div className="w-4 flex-shrink-0" />
                </div>
                <div className="text-center mt-6 px-5">
                    <button onClick={() => navigate('/template13/shop')} className="w-full py-3 border border-gray-900 text-gray-900 text-sm tracking-widest min-h-[48px]">VIEW ALL</button>
                </div>
            </section>

            {/* Desktop Products - EXACT original */}
            <section className="hidden lg:block w-full bg-gray-50 py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-4">All Products</h2>
                        <p className="text-xl text-gray-600">Discover our latest collection</p>
                    </div>

                    <div className="w-full max-w-none">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(id => (
                                <div key={id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-2xl hover:-translate-y-2 hover:border-gray-900 transition-all duration-300 group">
                                    <div className="aspect-[4/5] overflow-hidden relative bg-gray-50">
                                        <img
                                            src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`}
                                            alt={`Product ${id}`}
                                            className="object-cover object-center w-full h-full group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-4 lg:p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">Product Title {id}</h3>
                                        <p className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">${(69 + id * 10)}.00</p>
                                        <button className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-all duration-300">
                                            Add to Cart
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* === FEATURES / OUR PROMISE === */}
            {/* Mobile Features - horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16 bg-gray-50">
                <div className="flex overflow-x-auto gap-4 px-5 snap-x snap-mandatory scrollbar-hide">
                    {features.map((f, i) => (
                        <div key={i} className="min-w-[160px] flex-shrink-0 snap-start text-center py-6 px-4 bg-white rounded-2xl">
                            <div className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-3">
                                <f.icon className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-sm font-medium text-gray-900 mb-1">{f.title}</h3>
                            <p className="text-xs text-gray-500">{f.desc}</p>
                        </div>
                    ))}
                    <div className="w-4 flex-shrink-0" />
                </div>
            </section>

            {/* Desktop Our Promise - EXACT original */}
            <section className="hidden lg:block max-w-6xl mx-auto px-6 py-20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                    {[
                        { icon: Heart, title: 'Handmade', desc: 'Crafted with care by skilled artisans' },
                        { icon: Truck, title: 'Fair Trade', desc: 'Supporting communities worldwide' },
                        { icon: Package, title: 'Sustainable', desc: 'Eco-friendly materials and practices' }
                    ].map((item, index) => (
                        <div key={index} className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 text-white rounded-full mb-4">
                                <item.icon size={28} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                            <p className="text-gray-600">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* === BRAND STORY / ARTISAN STORY === */}
            {/* Mobile Brand Story - full-bleed stacked */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/3]">
                    <img src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920&q=80" alt="Our Story" className="w-full h-full object-cover" />
                </div>
                <div className="px-5 py-8 bg-gray-50">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">The Craft Behind the Product</h2>
                    <p className="text-[14px] text-gray-600 leading-relaxed mb-6">Our artisans use time-honored techniques combined with modern creativity to produce pieces that are not just products, but works of art. Each item carries the maker's signature and tells its own unique story.</p>
                    <button className="text-sm text-gray-900 border-b border-gray-900 pb-1 min-h-[44px] inline-flex items-center">Read Our Story</button>
                </div>
            </section>

            {/* Desktop Artisan Story - EXACT original */}
            <section className="hidden lg:block bg-gray-100 py-20">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <img
                        src="https://placehold.co/1200x400/E0E0E0/333333?text=Workshop"
                        alt="Workshop"
                        className="w-full h-64 object-cover rounded-sm mb-8 shadow-lg"
                    />
                    <h2 className="text-4xl font-bold text-gray-900 mb-6">The Craft Behind the Product</h2>
                    <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
                        Our artisans use time-honored techniques combined with modern creativity to produce
                        pieces that are not just products, but works of art. Each item carries the maker's
                        signature and tells its own unique story.
                    </p>
                </div>
            </section>

            {/* === TESTIMONIALS === */}
            {/* Mobile Testimonials - swipeable carousel */}
            <section className="lg:hidden py-10 sm:py-16 bg-white">
                <div className="px-5 mb-8">
                    <div className="text-center">
                        <p className="text-xs tracking-[0.3em] text-gray-400 mb-2 uppercase">Reviews</p>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">What Our Customers Say</h2>
                    </div>
                </div>
                <div className="overflow-hidden px-5" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                    <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                        {testimonials.map((t, i) => (
                            <div key={i} className="w-full flex-shrink-0 px-2">
                                <div className="bg-gray-50 rounded-2xl p-6 text-center">
                                    <div className="flex justify-center gap-1 mb-4">
                                        {[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-gray-800 text-gray-800" />)}
                                    </div>
                                    <p className="text-[14px] text-gray-600 leading-relaxed mb-5 italic">"{t.text}"</p>
                                    <p className="text-sm font-medium text-gray-900">{t.name}</p>
                                    <p className="text-xs text-gray-400">{t.location}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-center gap-2 mt-5">
                        {testimonials.map((_, i) => (
                            <button key={i} onClick={() => setActiveTestimonial(i)} className={`h-2 rounded-full transition-all ${activeTestimonial === i ? 'w-6 bg-gray-900' : 'w-2 bg-gray-300'}`} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop Testimonials - no testimonial section in original, so this section only shows on mobile */}

            {/* === NEWSLETTER === */}
            {/* Mobile Newsletter - stacked rounded */}
            <section className="lg:hidden py-10 sm:py-16 bg-white">
                <div className="max-w-2xl mx-auto px-5 text-center">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Join Our Craft Community</h3>
                    <p className="text-[14px] text-gray-600 mb-6">Get exclusive access to new collections and artisan stories</p>
                    <div className="flex flex-col gap-3">
                        <input type="email" placeholder="Enter your email" className="flex-1 px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-gray-900 text-gray-900 text-sm min-h-[48px]" />
                        <button className="px-8 py-4 bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors rounded-xl min-h-[48px]">Subscribe</button>
                    </div>
                </div>
            </section>

            {/* Desktop Newsletter - EXACT original */}
            <section className="hidden lg:block bg-white py-16">
                <div className="max-w-2xl mx-auto px-6 text-center">
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Join Our Craft Community</h3>
                    <p className="text-gray-600 mb-8">Get exclusive access to new collections and artisan stories</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-3 border-2 border-gray-200 rounded-sm focus:outline-none focus:border-gray-900 text-gray-900"
                        />
                        <button className="px-8 py-3 bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors rounded-sm">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* === INSTAGRAM - shared with responsive classes === */}
            <section className="py-10 sm:py-16 lg:py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-5 lg:px-8">
                    <div className="text-center mb-8 sm:mb-12">
                        <p className="text-xs tracking-[0.3em] text-gray-400 mb-2 uppercase">@essence_style</p>
                        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Follow the Aesthetic</h2>
                    </div>
                    <div className="grid grid-cols-3 lg:grid-cols-6 gap-1.5 sm:gap-2 lg:gap-3">
                        {instagramImages.map((img, i) => (
                            <div key={i} className="group relative aspect-square overflow-hidden rounded-lg cursor-pointer">
                                <img src={img} alt={`Instagram ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gray-900/0 group-hover:bg-gray-900/30 transition-colors duration-300 flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* === FOOTER === */}
            {/* Mobile Footer */}
            <footer className="lg:hidden bg-gray-900 text-white mt-0">
                <div className="px-4 sm:px-6 py-10 sm:py-12">
                    <div className="grid grid-cols-2 gap-6 sm:gap-8">
                        <div className="col-span-2">
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">ESSENCE</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template13')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template13/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template13/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template13/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-8 pt-6">
                        <p className="text-center text-gray-500 text-sm">&copy; 2025 ESSENCE. All rights reserved.</p>
                    </div>
                </div>
            </footer>

            {/* Desktop Footer - EXACT original */}
            <footer className="hidden lg:block bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">ESSENCE</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template13')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template13/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template13/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template13/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@essence.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">&copy; 2025 ESSENCE. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template13;